import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteSuspendedUsers, UserKeys } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import SharedDialog from '../../../../shared/shared-dialog';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  isAbleToSeeUIElement,
  ToolsRoles,
} from '../../../../utils/roles/client-roles';
import { DecomSuspendedUserModel } from '../../../../../types';
import { useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ErrorOutline from '@mui/icons-material/ErrorOutline';

interface PurgeSuspendedUsersDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedRows: string[];
  setSelectedRows: Dispatch<SetStateAction<string[]>>;
  changeLogRoles: string[];
  userList: DecomSuspendedUserModel[];
}

export default function PurgeSuspendedUsersDialog({
  open,
  handleClose,
  selectedRows,
  setSelectedRows,
  changeLogRoles,
  userList,
}: PurgeSuspendedUsersDialogProps) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [textConfirmation, setTextConfirmation] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const userId = searchParams.get('userId');

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      deleteSuspendedUsers(selectedModels, true, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message:
          selectedRows.length === 1
            ? 'The selected user has been purged from decom.'
            : 'The selected users have been purged from decom.',
      });

      selectedModels.forEach((model) => {
        if (model.userId === userId) {
          searchParams.delete('userId');
          setSearchParams(searchParams);
        }
      });
      queryClient.setQueryData(
        [UserKeys.SuspendedUsers],
        (oldData: DecomSuspendedUserModel[]) =>
          oldData.filter(
            (model) => !selectedRows.includes(model.userId + model.creationDate)
          )
      );
      handleClose();
      setSelectedRows([]);
      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          selectedRows.length === 1
            ? 'There was an issue purging the selected user from decom. Please refresh and try again.'
            : 'There was an issue purging the selected users from decom. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.automation.decommissioning.delete_suspended_users, {
        module: ModuleList.modules.automation.name,
        section: ModuleList.modules.automation.sections.decommissioning,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        ToolsRoles.DeleteSuspendedUsers.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  const selectedModels = userList.filter((user) =>
    selectedRows.includes(user.userId + user.creationDate)
  );

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={`Purge Decommissioning ${
        selectedRows.length === 1 ? 'User' : 'Users'
      }`}
      dialogContent={
        <Stack gap={2}>
          {selectedRows.length > 1 && (
            <Box>
              Purge <strong>{selectedModels.length} users</strong> from the
              decommissioning process? The decommissioning process will no
              longer run for the <strong>{selectedModels.length} users</strong>.
              <Box sx={{ display: 'flex', alignItems: 'initial' }}>
                <ErrorOutline
                  sx={{
                    color: 'error.main',
                    width: 16,
                    height: 16,
                    mr: 0.7,
                    mt: 2.3,
                  }}
                />
                <Typography variant="subtitle2" sx={{ mt: 2 }}>
                  Note: Any progress through the current decommissioning process
                  will be lost. Please enter "purge decom users" in the textbox
                  and click PURGE USERS to continue.
                </Typography>
              </Box>
            </Box>
          )}
          {selectedRows.length === 1 && (
            <Box>
              Purge <strong>{selectedModels[0].email}</strong> from the
              decommissioning process? The decommissioning process will no
              longer run for <strong>{selectedModels[0].email}</strong>.
              <Box sx={{ display: 'flex', alignItems: 'initial' }}>
                <ErrorOutline
                  sx={{
                    color: 'error.main',
                    width: 16,
                    height: 16,
                    mr: 0.7,
                    mt: 2.3,
                  }}
                />
                <Typography variant="subtitle2" sx={{ mt: 2 }}>
                  Note: Any progress through the current decommissioning process
                  will be lost. Please enter "purge decom users" in the textbox
                  and click PURGE USER to continue.
                </Typography>
              </Box>
            </Box>
          )}

          <TextField
            fullWidth
            label="Text Confirmation"
            placeholder="purge decom users"
            color="warning"
            value={textConfirmation}
            onChange={(e) => setTextConfirmation(e.target.value)}
          />
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        title: `Purge ${selectedRows.length === 1 ? 'User' : 'Users'}`,
        action: handleSubmit,
        loading: isPending,
        disabled:
          isPending ||
          textConfirmation.trim().toLowerCase() != 'purge decom users',
        color: 'error',
      }}
    />
  );
}
